import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { Portal, type ApiError, type AuthSession } from '@kick/types';
import { obtenerToken, useAuth } from './auth-store';

const baseURL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * Cliente HTTP de la tienda.
 *
 * `withCredentials` es obligatorio: el refresh token viaja en una cookie
 * httpOnly con Domain=.kick.test, que el navegador solo envía si se le pide
 * explícitamente incluir credenciales en peticiones a otro origen.
 */
export const api: AxiosInstance = axios.create({
  baseURL: `${baseURL}/api`,
  withCredentials: true,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

/** Cliente sin prefijo /api, para /health. */
export const rootApi: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 5_000,
});

api.interceptors.request.use((config) => {
  const token = obtenerToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  /**
   * Un envío con archivo no puede ir etiquetado como JSON.
   *
   * La instancia fija `application/json` para todo, que es lo correcto en casi
   * todas las peticiones. Pero con un `FormData` esa cabecera es mentira, y
   * además le falta la frontera que separa los campos: el servidor recibe algo
   * que no sabe leer y responde 400 sin poder explicar por qué.
   *
   * Aquí lo usa la subida del comprobante de transferencia. Va en el
   * interceptor y no en cada llamada porque acordarse en cada una es justo lo
   * que falla.
   */
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

/**
 * Renovación automática de la sesión.
 *
 * Si varias peticiones fallan a la vez con 401 se dispararía un refresh por
 * cada una, y como el token es rotativo el servidor interpretaría los
 * reintentos como reutilización y cerraría la sesión. Por eso solo se refresca
 * una vez y las demás esperan en cola a ese mismo resultado.
 */
let refrescando: Promise<string | null> | null = null;

async function refrescarSesion(): Promise<string | null> {
  try {
    const { data } = await axios.post<AuthSession>(
      `${baseURL}/api/auth/refresh`,
      /**
       * El portal viaja en la renovación.
       *
       * La cookie de sesión la comparten la tienda y el panel —mismo dominio
       * en producción, y en local las cookies ignoran el puerto—, así que sin
       * decirle al servidor desde dónde se pregunta, abrir uno heredaba la
       * sesión del otro: el administrador aparecía con sesión iniciada en la
       * tienda, con carrito y direcciones de cliente.
       */
      { portal: Portal.SHOP },
      { withCredentials: true },
    );
    useAuth.getState().iniciar(data);
    ultimoRefresco = Date.now();
    return data.accessToken;
  } catch (error) {
    /**
     * Solo se cierra la sesión si el servidor la rechazó de verdad.
     *
     * Antes cualquier fallo cerraba: un tropiezo de red al minimizar la
     * ventana, el portátil saliendo de reposo, un 502 de un reinicio del
     * API. Todo eso terminaba en la pantalla de login sin que la sesión
     * hubiera caducado. Ahora, si no hubo respuesta o fue un error del
     * servidor, se conserva lo que hay y se vuelve a intentar después.
     */
    const estado = (error as AxiosError)?.response?.status;
    const rechazada = estado === 401 || estado === 403 || estado === 400;
    if (rechazada) {
      useAuth.getState().cerrar();
      return null;
    }
    return useAuth.getState().accessToken;
  }
}

/**
 * Renovación preventiva: al volver a la pestaña tras horas, y cada tanto
 * mientras está abierta, se renueva antes de que caduque. Así el comercio
 * que deja el panel abierto todo el día nunca ve un 401 ni el login.
 */
let ultimoRefresco = 0;
const RENOVAR_CADA_MS = 6 * 60 * 60 * 1000;

function renovarSiToca(): void {
  if (!useAuth.getState().accessToken) return;
  if (Date.now() - ultimoRefresco < RENOVAR_CADA_MS) return;
  refrescando ??= refrescarSesion().finally(() => {
    refrescando = null;
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') renovarSiToca();
  });
  window.addEventListener('online', renovarSiToca);
  setInterval(renovarSiToca, 15 * 60 * 1000);
}

api.interceptors.response.use(
  (respuesta) => respuesta,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as InternalAxiosRequestConfig & { _reintentado?: boolean };
    const estado = error.response?.status;
    const codigo = error.response?.data?.code;

    const esRutaDeAuth =
      original?.url?.includes('/auth/refresh') || original?.url?.includes('/auth/login');

    if (estado === 401 && !original?._reintentado && !esRutaDeAuth) {
      original._reintentado = true;

      refrescando ??= refrescarSesion().finally(() => {
        refrescando = null;
      });

      const nuevoToken = await refrescando;
      if (nuevoToken) {
        original.headers.Authorization = `Bearer ${nuevoToken}`;
        return api(original);
      }
    }

    // La cuenta fue suspendida mientras la sesión estaba abierta.
    if (estado === 401 && codigo === 'USER_SUSPENDED') {
      useAuth.getState().cerrar();
    }

    return Promise.reject(error);
  },
);

/**
 * Intenta restaurar la sesión al cargar la página, usando la cookie de refresh.
 *
 * Pasa por la misma cola que el interceptor. Sin eso, el doble montaje de
 * React en desarrollo dispara dos refresh: el segundo usa un token ya
 * consumido, el servidor lo interpreta como robo y cierra la sesión. El
 * síntoma es que recargar la página saca al usuario.
 */
export async function restaurarSesion(): Promise<void> {
  refrescando ??= refrescarSesion().finally(() => {
    refrescando = null;
  });

  const token = await refrescando;
  if (!token) useAuth.getState().terminarCarga();
}

export function getErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError<ApiError>;
  if (axiosError?.response?.data?.message) return axiosError.response.data.message;
  if (axiosError?.code === 'ECONNABORTED') return 'La conexión tardó demasiado. Intenta de nuevo.';
  if (axiosError?.message === 'Network Error') return 'No pudimos conectarnos. Revisa tu internet.';
  return 'Ocurrió un error inesperado. Intenta de nuevo.';
}

export function getErrorCode(error: unknown): string | null {
  return (error as AxiosError<ApiError>)?.response?.data?.code ?? null;
}

/** Errores de validación por campo, para pintarlos en el formulario. */
export function getErrorDetails(error: unknown): Record<string, string[]> {
  return (error as AxiosError<ApiError>)?.response?.data?.details ?? {};
}
